import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faPlus, faTag } from '@fortawesome/free-solid-svg-icons'
import ImageContainer from '../image-container/image-container.component'

const TradePortalBrowse = ({ tradePosts, loading, gameData, playerData, gameElements }) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('browse')

  const postList = useMemo(
    () => Object.entries(tradePosts ?? {}).map(([docId, p]) => ({ ...p, docId })),
    [tradePosts]
  )

  const browsePosts = postList
    .filter(p => ['pending', 'awaiting_manager'].includes(p.status))
    .sort((a, b) => (b.ts_created ?? 0) - (a.ts_created ?? 0))

  const myPosts = postList
    .filter(p => p.posterId === playerData?.playerId && ['completed', 'cancelled', 'reversed'].includes(p.status))
    .sort((a, b) => (b.ts_created ?? 0) - (a.ts_created ?? 0))

  const myOpenPostsWithNewOffers = postList.filter(
    p => p.posterId === playerData?.playerId &&
    p.status === 'pending' &&
    Object.values(p.offers ?? {}).some(o => o.status === 'pending')
  ).length

  const displayed = activeTab === 'browse' ? browsePosts : myPosts
  const canPost = gameData?.access?.includes('tradePortal')

  return (
    <div className='tp-browse'>
      <div className='tp-browse-header'>
        <div className='tp-tabs'>
          <button
            className={`tp-tab ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
          >
            Browse
            {browsePosts.length > 0 && <span className='tp-tab-badge'>{browsePosts.length}</span>}
          </button>
          <button
            className={`tp-tab ${activeTab === 'my-posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-posts')}
          >
            My Trades
            {myOpenPostsWithNewOffers > 0 && <span className='tp-tab-badge alert'>{myOpenPostsWithNewOffers}</span>}
          </button>
        </div>
        {canPost && (
          <button className='g-button small-btn primary' onClick={() => navigate('newpost')}>
            <FontAwesomeIcon icon={faPlus} /> New Trade
          </button>
        )}
      </div>

      {loading ? (
        <div className='tp-empty'><ThreeDotsLoader /></div>
      ) : displayed.length === 0 ? (
        <div className='tp-empty'>
          <div className='tp-empty-icon'>🔄</div>
          <p>{activeTab === 'browse' ? 'No trades posted yet!' : "You haven't posted any trades yet."}</p>
          {activeTab === 'browse' && canPost && (
            <button className='g-button small-btn primary' onClick={() => navigate('newpost')}>
              Post the first trade!
            </button>
          )}
        </div>
      ) : (
        <div className='tp-post-grid'>
          {displayed.map(post => (
            <TradePostCard
              key={post.docId}
              post={post}
              gameElements={gameElements}
              playerData={playerData}
              onClick={() => navigate(`post/${post.docId}`)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function TradePostCard({ post, gameElements, playerData, onClick }) {
  const offeringEl = gameElements?.[post.offering?.itemId]
  const isOwn = post.posterId === playerData?.playerId
  const pendingOfferCount = Object.values(post.offers ?? {}).filter(o => o.status === 'pending').length
  const hasMyOffer = Object.values(post.offers ?? {}).some(o => o.offererId === playerData?.playerId)
  const wantItems = post.requesting ?? []

  const statusLabel = {
    pending: isOwn ? 'Your Trade' : 'Open',
    awaiting_manager: 'Pending',
    completed: 'Done',
    cancelled: 'Cancelled',
    reversed: 'Reversed',
  }[post.status] ?? post.status

  return (
    <div
      className={`tp-post-card tp-status-${post.status}${isOwn ? ' own' : ''}`}
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <span className={`tp-status-pill ${post.status}`}>{statusLabel}</span>

      <div className='tp-card-body'>
        <div className='tp-card-offering'>
          <div className='tp-icon-wrap large'>
            {offeringEl?.icon
              ? <ImageContainer src={offeringEl.icon} alt={offeringEl.name} />
              : <span className='tp-icon-placeholder'>?</span>
            }
            {post.offering?.quantity > 1 && (
              <span className='tp-qty-badge'>×{post.offering.quantity}</span>
            )}
          </div>
          <span className='tp-item-label'>{offeringEl?.name ?? 'Item'}</span>
        </div>

        <FontAwesomeIcon icon={faArrowRight} className='tp-card-arrow' />

        <div className='tp-card-wants'>
          {wantItems.length === 0 ? (
            <div className='tp-open-offer'>
              <FontAwesomeIcon icon={faTag} />
              <span>Open offer</span>
            </div>
          ) : (
            <>
              {wantItems.slice(0, 3).map((item, i) => {
                const el = item.itemId ? gameElements?.[item.itemId] : null
                return (
                  <div key={i} className='tp-icon-wrap small'>
                    {el?.icon
                      ? <ImageContainer src={el.icon} alt={el.name} />
                      : <span className='tp-icon-placeholder small'>
                          {item.type === 'currency' 
                          ? <ImageContainer src="/images/icons/coin.png" className='icon' /> 
                          : item.type === 'points' 
                          ? <ImageContainer src='/images/icons/xp.png' className='icon' /> 
                          : '?'}
                        </span>
                    }
                    {item.quantity > 1 && <span className='tp-qty-badge small'>×{item.quantity}</span>}
                  </div>
                )
              })}
              {wantItems.length > 3 && (
                <span className='tp-more-wants'>+{wantItems.length - 3}</span>
              )}
            </>
          )}
        </div>
      </div>

      <div className='tp-card-footer'>
        <span className='tp-poster-name'>{post.displayName}</span>
        <div className='tp-card-chips'>
          {pendingOfferCount > 0 && (
            <span className='tp-offers-chip'>
              {pendingOfferCount} offer{pendingOfferCount !== 1 ? 's' : ''}
            </span>
          )}
          {hasMyOffer && !isOwn && (
            <span className='tp-my-offer-chip'>✓ Offered</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default TradePortalBrowse
